import React, { useEffect, useState } from "react";
import { Country, State, City } from "country-state-city";
import { getLocationObject } from "../../utils/getLocationObject";
import { MapPinIcon } from "lucide-react";

const LocationSelector = ({ onChange, defaultValue = {} }) => {
    const defaultCountry = defaultValue.country || "";
    const defaultState = defaultValue.state || "";
    const defaultCity = defaultValue.city || "";
    const [countries] = useState(Country.getAllCountries());
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);

    const [selected, setSelected] = useState({
        country: defaultCountry,
        state: defaultState,
        city: defaultCity,
    });

    useEffect(() => {
        setSelected((prev) => {
            if (
                prev.country === defaultCountry &&
                prev.state === defaultState &&
                prev.city === defaultCity
            ) {
                return prev;
            }

            return {
                country: defaultCountry,
                state: defaultState,
                city: defaultCity,
            };
        });
    }, [defaultCountry, defaultState, defaultCity]);

    useEffect(() => {
        const country = countries.find(c => c.name === selected.country);
        if (country) {
            setStates(State.getStatesOfCountry(country.isoCode));
            return;
        }

        setStates([]);
    }, [selected.country, countries]);

    useEffect(() => {
        const country = countries.find(c => c.name === selected.country);
        const state = states.find(s => s.name === selected.state);
        if (country && state) {
            setCities(City.getCitiesOfState(country.isoCode, state.isoCode));
            return;
        }

        setCities([]);
    }, [selected.state, selected.country, states]);

    const handleCountryChange = (e) => {
        const country = countries.find(c => c.isoCode === e.target.value);
        setSelected({ country: country?.name || "", state: "", city: "" });
        setStates(State.getStatesOfCountry(e.target.value));
        setCities([]);
        onChange({ countryName: country?.name, stateName: "", cityName: "" });
    };

    const handleStateChange = (e) => {
        const state = states.find(s => s.isoCode === e.target.value);
        setSelected(prev => ({ ...prev, state: state?.name || "", city: "" }));
        const country = countries.find(c => c.name === selected.country);
        setCities(City.getCitiesOfState(country?.isoCode, e.target.value));
        onChange({ countryName: selected.country, stateName: state?.name, cityName: "" });
    };

    const handleCityChange = (e) => {
        const cityName = e.target.value;
        setSelected(prev => ({ ...prev, city: cityName }));

        const loc = getLocationObject({
            countryName: selected.country,
            stateName: selected.state,
            cityName,
        });

        if (loc) {
            onChange({
                countryName: loc.country,
                stateName: loc.state,
                cityName: loc.city,
            });
        }
    };

    return (
        <>
            <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Country*</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MapPinIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                        value={countries.find(c => c.name === selected.country)?.isoCode || ""}
                        onChange={handleCountryChange}
                        required
                        className="focus:outline-none focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md p-2 border"
                    >
                        <option value="">Select Country</option>
                        {countries.map(c => (
                            <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">State*</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MapPinIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                        value={states.find(s => s.name === selected.state)?.isoCode || ""}
                        onChange={handleStateChange}
                        disabled={!states.length}
                        required
                        className="focus:outline-none focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md p-2 border"
                    >
                        <option value="">Select State</option>
                        {states.map(s => (
                            <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">City*</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MapPinIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                        value={selected.city}
                        onChange={handleCityChange}
                        disabled={!cities.length}
                        required
                        className="focus:outline-none focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md p-2 border"
                    >
                        <option value="">Select City</option>
                        {cities.map(c => (
                            <option key={c.name} value={c.name}>{c.name}</option>
                        ))}
                    </select>
                </div>
            </div>
        </>
    );
};

export default LocationSelector;
